import React, { useEffect, useCallback, useRef, useMemo, useReducer, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Home, FileText, ChevronDown, ChevronUp,
  List, ClipboardList, Settings, PlusCircle,
  KeyRound, Layers, Ticket, Wrench,
  X, BarChart3, Users, Server, Shield, Monitor,
  ChevronRight, Building, LifeBuoy, ChevronLeft,
  ExternalLink, Book, Laptop, HardDrive,
  FileCheck
} from 'lucide-react';
import { Spin, Tooltip } from 'antd';
import clsx from 'clsx';
import './Sidebar.css';
import { useAuth } from '../../../context/AuthContext';
import { PERMISSION_LEVELS } from '../../../const/roles';
import { useAppContext } from '../../../context/AppContext';

const DEFAULT_APP_TITLE = "Service Desk";

const Sidebar = ({ isOpen, isCollapsed, isMobile, toggleSidebar, toggleCollapse, user }) => {
  const location = useLocation();
    const { 
    hasAnyRole, 
    hasPermission,
    PERMISSION_LEVELS,
    initialized,
    hasClientPortalAccess   // 🆕 for Client Computing category gating
  } = useAuth();
  const { appSettings, loadingAppSettings } = useAppContext();

  // Local state for sidebar functionality
  const [activeCategory, setActiveCategory] = useState(null);
  const [tooltip, setTooltip] = useState({ show: false, content: '', x: 0, y: 0 });
  const [accessibleMenu, setAccessibleMenu] = useState([]);
  
  const sidebarRef = useRef(null);

  // Helper function to check if user is main contractor
  const isMainContractor = user?.isMainContractor === true;
  const hasContracts = user?.mainContractorContractIds?.length > 0;
  const isPartnerMainContractor = isMainContractor && hasContracts;

    // Check access function - memoized to prevent recreation
  const checkAccess = useCallback((requiredRoles = [], requiredPermissions = [], requiresClientPortalAccess = false, excludeRoles = []) => {
    // 🆕 Client Computing gate: if required, user MUST have EUC contract access
    if (requiresClientPortalAccess && !hasClientPortalAccess) {
      return false;
    }

    // 🆕 Exclusion: if user has any excluded role, deny access
    if (excludeRoles.length > 0 && hasAnyRole(excludeRoles)) {
      return false;
    }

    if (requiredRoles.length === 0 && requiredPermissions.length === 0) return true;
    
    const roleAccess = requiredRoles.length === 0 || 
      hasAnyRole(requiredRoles);
    
    const permissionAccess = requiredPermissions.length === 0 || 
      requiredPermissions.every(({ resource, action, level }) => 
        hasPermission(resource, action, level || PERMISSION_LEVELS.OWN)
      );
    
    return roleAccess || permissionAccess;
  }, [hasAnyRole, hasPermission, PERMISSION_LEVELS, hasClientPortalAccess]);

  // Menu structure organized by categories - DYNAMIC based on user type
  const menuCategories = useMemo(() => {
    // Determine dynamic paths based on user type
    const dashboardPath = isPartnerMainContractor ? '/dashboard/partner' : '/dashboard';
    const ticketsPath = isPartnerMainContractor ? '/partner/tickets' : '/tickets';
    const myTicketsPath = isPartnerMainContractor ? '/partner/my-tickets' : '/my-tickets';
    
    return [
      {
        id: 'dashboard',
        label: 'Overview',
        icon: Home,
        items: [
          {
            type: 'link',
            path: dashboardPath,
            icon: Home,
            label: 'Dashboard',
            requiredRoles: [],
            requiredPermissions: []
          }
        ]
      },
      {
        id: 'reports',
        label: 'Analytics & Reports',
        icon: BarChart3,
        items: [
          {
            type: 'link',
            path: '/reports',
            icon: BarChart3,
            label: 'Reports Hub',
            requiredRoles: ['SYSTEM_ADMIN', 'INTERNAL_SUPERVISOR', 'PARTNER_SUPERVISOR'],
            requiredPermissions: []
          },
        ]
      },
      {
        id: 'service',
        label: 'Service Management',
        icon: LifeBuoy,
        items: [
          { 
            type: 'link', 
            path: ticketsPath, 
            icon: List, 
            label: 'All Tickets',
            requiredRoles: [],
            requiredPermissions: [
              { resource: 'tickets', action: 'view', level: PERMISSION_LEVELS.OWN }
            ]
          },
          { 
            type: 'link', 
            path: '/tickets/new', 
            icon: PlusCircle, 
            label: 'Create New Ticket',
            requiredRoles: [],
            requiredPermissions: [
              { resource: 'tickets', action: 'edit', level: PERMISSION_LEVELS.OWN }
            ]
          },
          { 
            type: 'link', 
            path: myTicketsPath, 
            icon: ClipboardList, 
            label: 'My Assigned Tickets',
            requiredRoles: [],
            requiredPermissions: [
              { resource: 'tickets', action: 'view', level: PERMISSION_LEVELS.OWN }
            ]
          },
        ]
      },
      // 🆕 Client Computing Portal
      {
        id: 'client-computing',
        label: 'Client Computing',
        icon: Laptop,
        requiredRoles: [],
        requiredPermissions: [],
        requiresClientPortalAccess: true,   // gate: only users with EUC access
        items: [
          {
            type: 'link',
            path: '/client-computing/assets',
            icon: HardDrive,
            label: 'Client Assets',
            requiredRoles: ['SYSTEM_ADMIN', 'INTERNAL_SUPERVISOR'],
            requiredPermissions: [
              { resource: 'settings', action: 'edit', level: PERMISSION_LEVELS.ALL }
            ]
          }
          // Future items will be added here (Dashboard, My Devices, My Tickets, etc.)
        ]
      },
      // 🆕 CR Management
      {
        id: 'cr-management',
        label: 'CR Management',
        icon: FileCheck,
        requiredRoles: [
          'SYSTEM_ADMIN',
          'INTERNAL_SUPERVISOR',
          'INTERNAL_STANDARD',
          'PARTNER_SUPERVISOR',
          'PARTNER_STANDARD',
          'CUSTOMER_SUPERVISOR',
          'CUSTOMER_STANDARD'
        ],
        requiredPermissions: [],
        items: [
          {
            type: 'link',
            path: '/cr-management',
            icon: List,
            label: 'CR List',
            requiredRoles: [
              'SYSTEM_ADMIN',
              'INTERNAL_SUPERVISOR',
              'INTERNAL_STANDARD',
              'PARTNER_SUPERVISOR',
              'PARTNER_STANDARD',
              'CUSTOMER_SUPERVISOR',
              'CUSTOMER_STANDARD'
            ],
            requiredPermissions: []
          },
          {
            type: 'link',
            path: '/cr-management/create',
            icon: PlusCircle,
            label: 'Create CR',
            requiredRoles: [
              'SYSTEM_ADMIN',
              'INTERNAL_SUPERVISOR',
              'INTERNAL_STANDARD',
              'PARTNER_SUPERVISOR',
              'PARTNER_STANDARD'
            ],
            requiredPermissions: [],
            excludeRoles: [
              'CUSTOMER_SUPERVISOR', 'CUSTOMER_STANDARD', 'CUSTOMER_GLOBAL_VIEWER',
              'Customer Supervisor', 'Customer Standard', 'Customer Global Viewer'
            ]
          }
        ]
      },
      {
        id: 'administration',
        label: 'Administration',
        icon: Settings,
        requiredRoles: ['SYSTEM_ADMIN', 'INTERNAL_SUPERVISOR'],
        requiredPermissions: [
          { resource: 'settings', action: 'edit', level: PERMISSION_LEVELS.ALL }
        ],
        items: [
          {
            type: 'link',
            path: '/admin/user-access',
            icon: Users,
            label: 'User Management',
            requiredRoles: ['SYSTEM_ADMIN', 'INTERNAL_SUPERVISOR'],
            requiredPermissions: [
              { resource: 'settings', action: 'edit', level: PERMISSION_LEVELS.ALL }
            ]
          },
          {
            type: 'link',
            path: '/admin/service-catalog',
            icon: Layers,
            label: 'Service Catalog',
            requiredRoles: ['SYSTEM_ADMIN', 'INTERNAL_SUPERVISOR'],
            requiredPermissions: [
              { resource: 'settings', action: 'edit', level: PERMISSION_LEVELS.ALL }
            ]
          },
          {
            type: 'link',
            path: '/admin/ticket-settings',
            icon: Ticket,
            label: 'Ticket Configuration',
            requiredRoles: ['SYSTEM_ADMIN', 'INTERNAL_SUPERVISOR'],
            requiredPermissions: [
              { resource: 'settings', action: 'edit', level: PERMISSION_LEVELS.ALL }
            ]
          },
          {
            type: 'link',
            path: '/admin/system-automation',
            icon: Monitor,
            label: 'System Automation',
            requiredRoles: ['SYSTEM_ADMIN', 'INTERNAL_SUPERVISOR'],
            requiredPermissions: [
              { resource: 'settings', action: 'edit', level: PERMISSION_LEVELS.ALL }
            ]
          }
        ]
      },
      {
        id: 'knowledge-base',
        label: 'Knowledge Base',
        icon: Book,
        items: [
          {
            type: 'link',
            path: '/knowledge-base',
            icon: Book,
            label: 'Browse Articles',
            requiredRoles: [],
            requiredPermissions: []
          },
          {
            type: 'link',
            path: '/knowledge-base/new',
            icon: PlusCircle,
            label: 'Create Article',
            requiredRoles: [],
            requiredPermissions: []
          }
        ]
      },
      {
        id: 'tools',
        label: 'Tools & Security',
        icon: Shield,
        items: [
          {
            type: 'link',
            path: '/staff/tools',
            icon: Wrench,
            label: 'IT Tools',
            requiredRoles: [],
            requiredPermissions: [
              { resource: 'tickets', action: 'edit', level: PERMISSION_LEVELS.ASSIGNED }
            ],
            isComingSoon: true,
            comingSoonMessage: 'IT Tools - Coming Soon'
          },
          {
            type: 'link',
            path: '/admin/security',
            icon: Shield,
            label: 'Security Center',
            requiredRoles: ['SYSTEM_ADMIN'],
            requiredPermissions: [
              { resource: 'security', action: 'view', level: PERMISSION_LEVELS.ALL }
            ],
            isComingSoon: true,
            comingSoonMessage: 'Security Center - Coming Soon'
          }
        ]
      },
    ];
  }, [isPartnerMainContractor, PERMISSION_LEVELS]);

  // External Links category - conditionally included only for non-main-contractors
  const externalLinksCategory = useMemo(() => {
    // Hide External Links for main contractors
    if (isPartnerMainContractor) {
      return null;
    }
    
    return {
      id: 'external-links',
      label: 'External Links',
      icon: ExternalLink,
      requiredRoles: [
        'SYSTEM_ADMIN', 
        'INTERNAL_SUPERVISOR', 
        'INTERNAL_STANDARD', 
        'PARTNER_SUPERVISOR', 
        'PARTNER_STANDARD'
      ],
      requiredPermissions: [],
      items: [
        {
          type: 'link',
          path: '/external/yps-box',
          icon: Server,
          label: 'YPS Box',
          requiredRoles: [
            'SYSTEM_ADMIN', 
            'INTERNAL_SUPERVISOR', 
            'INTERNAL_STANDARD', 
            'PARTNER_SUPERVISOR', 
            'PARTNER_STANDARD'
          ],
          requiredPermissions: [],
          isExternal: true
        },
        {
          type: 'link',
          path: '/external/yps-webmail',
          icon: FileText,
          label: 'YPS Web Mail',
          requiredRoles: [
            'SYSTEM_ADMIN', 
            'INTERNAL_SUPERVISOR', 
            'INTERNAL_STANDARD', 
            'PARTNER_SUPERVISOR', 
            'PARTNER_STANDARD'
          ],
          requiredPermissions: [],
          isExternal: true
        },
        {
          type: 'link',
          path: '/external/yps-calendar',
          icon: FileText,
          label: 'YPS Calendar',
          requiredRoles: [
            'SYSTEM_ADMIN', 
            'INTERNAL_SUPERVISOR', 
            'INTERNAL_STANDARD', 
            'PARTNER_SUPERVISOR', 
            'PARTNER_STANDARD'
          ],
          requiredPermissions: [],
          isExternal: true
        }
      ]
    };
  }, [isPartnerMainContractor]);

  // Combine menu categories with external links (if not null)
  const allMenuCategories = useMemo(() => {
    const categories = [...menuCategories];
    if (externalLinksCategory) {
      categories.push(externalLinksCategory);
    }
    return categories;
  }, [menuCategories, externalLinksCategory]);

  // Compute accessible menu once when dependencies change
  useEffect(() => {
    if (!initialized) return;

        const filteredMenu = allMenuCategories
      .map(category => {
        // Check category-level access (including Client Portal gate if required)
        const categoryAccess = checkAccess(
          category.requiredRoles,
          category.requiredPermissions,
          category.requiresClientPortalAccess,
          category.excludeRoles || []
        );
        
        if (!categoryAccess) return null;

        // Filter items within accessible category
        const accessibleItems = category.items.filter(item => 
          checkAccess(
            item.requiredRoles,
            item.requiredPermissions,
            item.requiresClientPortalAccess,
            item.excludeRoles || []
          )
        );

        // Only include category if it has accessible items
        return accessibleItems.length > 0 ? {
          ...category,
          items: accessibleItems
        } : null;
      })
      .filter(Boolean); // Remove null categories

    setAccessibleMenu(filteredMenu);
  }, [allMenuCategories, checkAccess, initialized]);

  const handleNavLinkClick = useCallback(() => {
    if (isMobile && isOpen) {
      toggleSidebar();
    }
  }, [isMobile, isOpen, toggleSidebar]);

  const isSubmenuParentActive = useCallback((item) => {
    if (item.type === 'link') {
      const itemPathBase = item.path.replace('/:id', '').split('?')[0];
      return location.pathname.startsWith(itemPathBase);
    }
    return false;
  }, [location.pathname]);

  const handleMouseEnter = useCallback((e, isComingSoon = false, comingSoonMessage = '', isDisabled = false, requiredRoles = [], requiredPermissions = []) => {
    if (isMobile) return;
    
    let content = '';
    
    if (isComingSoon) {
      content = comingSoonMessage || 'Coming Soon';
    } else if (isDisabled) {
      const roleMessage = requiredRoles.length > 0 
        ? `Requires role(s): ${requiredRoles.join(' or ')}` 
        : '';
      
      const permissionMessage = requiredPermissions.length > 0
        ? `Requires permission(s): ${requiredPermissions.map(p => 
            `${p.resource}.${p.action} (${PERMISSION_LEVELS[p.level] || p.level})`
          ).join(', ')}`
        : '';
      
      content = [roleMessage, permissionMessage]
        .filter(Boolean)
        .join('\n') || "Access Denied";
    }
    
    if (content) {
      const rect = e.currentTarget.getBoundingClientRect();
      setTooltip({
        show: true,
        content,
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height + window.scrollY + 5,
      });
    }
  }, [PERMISSION_LEVELS, isMobile]);

  const handleMouseLeave = useCallback(() => {
    if (!isMobile) {
      setTooltip(prev => ({ ...prev, show: false }));
    }
  }, [isMobile]);

  const handleCategoryClick = useCallback((categoryId) => {
    if (isCollapsed) {
      toggleSidebar();
      setTimeout(() => {
        setActiveCategory(categoryId);
      }, 300);
    } else {
      if (activeCategory === categoryId) {
        setActiveCategory(null);
      } else {
        setActiveCategory(categoryId);
      }
    }
  }, [activeCategory, isCollapsed, toggleSidebar]);

  const handleDisabledClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // Handle external link clicks
  const handleExternalLinkClick = useCallback((e, item) => {
    e.preventDefault();
    
    // External URLs configuration
    const externalUrls = {
      '/external/yps-box': 'https://nas.yps.com.my',
      '/external/yps-webmail': 'https://webmail.yps.com.my',
      '/external/yps-calendar': 'https://calendar.yps.com.my'
    };
    
    const externalUrl = externalUrls[item.path];
    if (externalUrl) {
      window.open(externalUrl, '_blank', 'noopener,noreferrer');
    }
    
    if (isMobile && isOpen) {
      toggleSidebar();
    }
  }, [isMobile, isOpen, toggleSidebar]);

  // Auto-detect active category based on current route - UPDATED for partner paths
  useEffect(() => {
    if (location.pathname && accessibleMenu.length > 0) {
      const currentPath = location.pathname;
      
      // Normalize paths for comparison (handle partner routes)
      let normalizedPath = currentPath;
      if (currentPath === '/dashboard/partner') normalizedPath = '/dashboard';
      if (currentPath === '/partner/tickets') normalizedPath = '/tickets';
      if (currentPath === '/partner/my-tickets') normalizedPath = '/my-tickets';
      
      for (const category of accessibleMenu) {
        for (const item of category.items) {
          if (item.type === 'link') {
            let itemPathBase = item.path.replace('/:id', '').split('?')[0];
            
            // Normalize item path for comparison
            if (itemPathBase === '/dashboard/partner') itemPathBase = '/dashboard';
            if (itemPathBase === '/partner/tickets') itemPathBase = '/tickets';
            if (itemPathBase === '/partner/my-tickets') itemPathBase = '/my-tickets';
            
            if (normalizedPath.startsWith(itemPathBase)) {
              setActiveCategory(category.id);
              return;
            }
          }
        }
      }
    }
  }, [location.pathname, accessibleMenu]);

  const renderMenuItem = useCallback((item, isSecondary = false) => {
    const isDisabled = item.isComingSoon;
    const isExternal = item.isExternal;

    if (item.type === 'link') {
      const menuItemContent = (
        <NavLink
          to={item.path}
          className={({ isActive }) => clsx('nav-item', { 
            active: isActive,
            'coming-soon': item.isComingSoon,
            'external-link': isExternal,
            'nav-item-secondary': isSecondary,
            'nav-item-collapsed': isCollapsed && !isSecondary
          })}
          aria-disabled={isDisabled}
          onClick={(e) => {
            if (item.isComingSoon) {
              handleDisabledClick(e);
            } else if (isExternal) {
              handleExternalLinkClick(e, item);
            } else {
              handleNavLinkClick();
              if (isMobile) {
                toggleSidebar();
              }
            }
          }}
        >
          <div className="nav-icon-wrapper">
            <item.icon className="nav-icon" aria-hidden="true" />
            {isExternal && !isCollapsed && (
              <ExternalLink size={12} className="external-link-indicator" />
            )}
          </div>
          {!isCollapsed && (
            <div className="nav-text-wrapper">
              <span className="nav-text">{item.label}</span>
              {item.isComingSoon && (
                <span className="coming-soon-badge">Soon</span>
              )}
              {isExternal && (
                <span className="external-link-badge">External</span>
              )}
            </div>
          )}
        </NavLink>
      );

      if (isCollapsed && !isSecondary) {
        return (
          <Tooltip 
            key={item.path}
            title={item.label} 
            placement="right"
            classNames={{ root: "sidebar-tooltip" }}
          >
            <div
              className={clsx('nav-item-wrapper', { 
                disabled: isDisabled,
                'external-link-wrapper': isExternal
              })}
              onMouseEnter={(e) => handleMouseEnter(
                e, 
                item.isComingSoon, 
                item.comingSoonMessage,
                false,
                [],
                []
              )}
              onMouseLeave={handleMouseLeave}
              onClick={isDisabled ? handleDisabledClick : undefined}
            >
              {menuItemContent}
            </div>
          </Tooltip>
        );
      }

      return (
        <div
          key={item.path}
          className={clsx('nav-item-wrapper', { 
            disabled: isDisabled,
            'external-link-wrapper': isExternal
          })}
          onMouseEnter={(e) => handleMouseEnter(
            e, 
            item.isComingSoon, 
            item.comingSoonMessage,
            false,
            [],
            []
          )}
          onMouseLeave={handleMouseLeave}
          onClick={isDisabled ? handleDisabledClick : undefined}
        >
          {menuItemContent}
        </div>
      );
    }

    return null;
  }, [isCollapsed, isMobile, handleDisabledClick, handleNavLinkClick, handleExternalLinkClick, toggleSidebar, handleMouseEnter, handleMouseLeave]);

  const renderCategory = useCallback((category) => {
    const isActive = activeCategory === category.id;
    const hasActiveChild = category.items.some(item => 
      item.type === 'link' && isSubmenuParentActive(item)
    );

    const categoryContent = (
      <button
        className={clsx('category-header', {
          active: isActive || hasActiveChild,
          'has-active-child': hasActiveChild,
          'category-collapsed': isCollapsed
        })}
        onClick={() => handleCategoryClick(category.id)}
        onMouseEnter={(e) => handleMouseEnter(e, false, '', false, [], [])}
        onMouseLeave={handleMouseLeave}
      >
        <div className="category-header-content">
          <div className="category-icon-wrapper">
            <category.icon className="category-icon" aria-hidden="true" />
          </div>
          {!isCollapsed && (
            <>
              <span className="category-label">{category.label}</span>
              <ChevronRight className={clsx('category-chevron', { rotated: isActive })} />
            </>
          )}
        </div>
      </button>
    );

    if (isCollapsed) {
      return (
        <Tooltip 
          key={category.id}
          title={category.label} 
          placement="right"
          classNames={{ root: "sidebar-tooltip" }}
        >
          <div className="sidebar-category">
            {categoryContent}
          </div>
        </Tooltip>
      );
    }

    return (
      <div key={category.id} className="sidebar-category">
        {categoryContent}
        
        {isActive && !isCollapsed && (
          <div className="category-content">
            {category.items.map(item => renderMenuItem(item))}
          </div>
        )}
      </div>
    );
  }, [activeCategory, isSubmenuParentActive, handleCategoryClick, handleMouseEnter, handleMouseLeave, isCollapsed, renderMenuItem]);

  if (!initialized || loadingAppSettings) {
    return (
      <aside className={clsx('app-sidebar', 'dual-tier-sidebar', 'sidebar-open')}>
        <div className="sidebar-header" style={{ justifyContent: 'center' }}>
          <Spin size="small" />
        </div>
        <nav className="sidebar-nav">
          <div className="nav-item">Loading Menu...</div>
        </nav>
      </aside>
    );
  }

  return (
    <>
      <aside 
        ref={sidebarRef}
        className={clsx(
          'app-sidebar', 
          'dual-tier-sidebar', 
          isOpen ? 'sidebar-open' : 'sidebar-closed',
          isCollapsed && 'sidebar-collapsed'
        )}
      >
        {isMobile && isOpen && (
          <button 
            className="sidebar-close-btn"
            onClick={toggleSidebar}
            aria-label="Close sidebar"
          >
            <X size={20} />
          </button>
        )}
        
        <div className="sidebar-header">
          <div className="app-brand">
            {appSettings?.companyLogoUrl && (
              <div className="app-logo-container">
                <img
                  src={appSettings.companyLogoUrl}
                  alt={appSettings?.appTitle || DEFAULT_APP_TITLE}
                  className="app-logo"
                  onError={(e) => { 
                    e.target.onerror = null; 
                    e.target.src = "https://placehold.co/40x40/1e3a8a/ffffff?text=IT" 
                  }}
                />
              </div>
            )}
            {!isCollapsed && (
              <div className="app-brand-text">
                <span className="app-title">{appSettings?.appTitle || DEFAULT_APP_TITLE}</span>
                {user?.role && (
                  <span className="user-role-badge">{user.role.replace('_', ' ')}</span>
                )}
              </div>
            )}
          </div>
          
          {/* Collapse Toggle Button - Desktop only */}
          {!isMobile && (
            <button 
              className="sidebar-collapse-btn"
              onClick={() => {
                console.log('Collapse button clicked');
                if (toggleCollapse) {
                  toggleCollapse();
                } else {
                  toggleSidebar();
                }
              }}
              aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            </button>
          )}
        </div>

        <div className="sidebar-content">
          <nav className="sidebar-nav-primary" aria-label="Main navigation">
            {accessibleMenu.map(renderCategory)}
          </nav>

          {activeCategory && !isCollapsed && (
            <div className="sidebar-secondary">
              <div className="secondary-header">
                <button 
                  className="secondary-back-btn"
                  onClick={() => setActiveCategory(null)}
                  aria-label="Back to main menu"
                >
                  <ChevronLeft size={16} />
                </button>
                <h3>
                  {accessibleMenu.find(cat => cat.id === activeCategory)?.label}
                </h3>
              </div>
              <nav className="sidebar-nav-secondary" aria-label="Secondary navigation">
                {accessibleMenu
                  .find(cat => cat.id === activeCategory)
                  ?.items.map(item => renderMenuItem(item, true))}
              </nav>
            </div>
          )}
        </div>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </div>
            {!isCollapsed && (
              <div className="user-details">
                <span className="user-name">{user?.firstName} {user?.lastName}</span>
                <span className="user-email">{user?.email}</span>
              </div>
            )}
          </div>
        </div>

        {tooltip.show && !isMobile && (
          <div
            className="app-tooltip show"
            role="tooltip"
            style={{
              left: `${tooltip.x}px`,
              top: `${tooltip.y}px`,
              transform: 'translateX(-50%)',
            }}
          >
            {tooltip.content}
          </div>
        )}
      </aside>

      {/* Mobile overlay */}
      {isMobile && isOpen && (
        <div className="sidebar-overlay" onClick={toggleSidebar} />
      )}
    </>
  );
};

export default React.memo(Sidebar);